import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listArtists, toggleSubscribe, resolveFileUrl } from '../lib/api';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';
import { Music, Search as SearchIcon, UserPlus, UserCheck, Play, Loader2 } from 'lucide-react';

export default function Artists() {
  const { user } = useApp();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    (async () => {
      try { setArtists(await listArtists({ limit: 80 })); } catch { toast.error('Could not load artists'); }
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return artists;
    return artists.filter(a => (a.name || '').toLowerCase().includes(s) || (a.genre || '').toLowerCase().includes(s));
  }, [q, artists]);

  const follow = async (a) => {
    if (!user) { toast.error('Sign in to follow'); return; }
    if (!a.channel_id) { toast.info('Artist has no channel yet'); return; }
    try {
      const r = await toggleSubscribe(a.channel_id);
      setArtists(list => list.map(x => x.artist_id === a.artist_id ? { ...x, following: !!r.subscribed } : x));
      toast.success(r.subscribed ? `Following ${a.name}` : `Unfollowed ${a.name}`);
    } catch { toast.error('Could not update follow'); }
  };

  return (
    <div data-testid="artists-page" className="max-w-[1200px] mx-auto">
      <div className="flex items-start md:items-center justify-between flex-col md:flex-row gap-3 md:gap-4">
        <div className="min-w-0">
          <div className="chip active"><Music className="w-3 h-3 mr-1 inline" /> Independent artists</div>
          <h1 className="font-display text-[28px] sm:text-[34px] md:text-[44px] mt-2 leading-tight">Discover who's on <span className="brand-gradient">ReaperCast</span>.</h1>
          <p className="text-white/65 text-[13px] md:text-[14px] mt-1 max-w-[640px]">Real working musicians. Follow what you like — new drops surface in your reel.</p>
        </div>
        <Link to="/feed" className="btn-primary shrink-0" data-testid="artists-open-reel"><Play className="w-4 h-4" /> Open the reel</Link>
      </div>

      <label className="mt-5 md:mt-6 flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-full px-4 py-2.5 max-w-[440px] focus-within:border-[#a78bfa]/60">
        <SearchIcon className="w-4 h-4 text-white/50" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name or genre" className="bg-transparent flex-1 focus:outline-none text-[14px] placeholder:text-white/40" data-testid="artists-search" />
      </label>

      {loading ? (
        <div className="mt-10 grid place-items-center text-white/50"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4 landscape:max-[900px]:grid-cols-4">
          {filtered.map(a => (
            <div key={a.artist_id} className="card-shell p-4 hover-lift" data-testid={`artist-card-${a.artist_id}`}>
              <div className="aspect-square rounded-xl overflow-hidden relative border border-white/10">
                {a.cover_url || a.avatar ? (
                  <img src={resolveFileUrl(a.cover_url || a.avatar)} alt={a.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center" style={{ background: 'linear-gradient(135deg,#a78bfa,#22d3ee)' }}>
                    <Music className="w-10 h-10 text-white/70" />
                  </div>
                )}
              </div>
              <div className="mt-3">
                <div className="font-display text-[16px] truncate">{a.name}</div>
                <div className="text-[12px] text-white/55 truncate">{a.genre || '—'} · {a.track_count} track{a.track_count === 1 ? '' : 's'}</div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Link to={a.channel_id ? `/channel/${a.channel_id}` : '/feed'} className="btn-ghost flex-1 text-[12.5px] justify-center">Visit</Link>
                <button onClick={() => follow(a)} className={`w-9 h-9 rounded-full grid place-items-center border ${a.following ? 'bg-[#22d3ee]/70 border-[#22d3ee] text-white' : 'bg-white/5 border-white/10 hover:bg-white/10'}`} data-testid={`artist-follow-${a.artist_id}`}>
                  {a.following ? <UserCheck className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
          {!filtered.length && <div className="text-white/50 col-span-full py-10 text-center">No artists match "{q}".</div>}
        </div>
      )}
    </div>
  );
}
